// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { FC } from "react";
import { css } from "styled-components";
import { QRCodeCanvas, QRCodeSVG } from "qrcode.react";
import Img from "@insite/mobius/Img";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { isFirefox, isSafariDesktop } from "@eisinc/App/Utilities/helpers";

// eslint-disable-next-line
const Barcode = require("react-barcode");

const BOXES_PER_PAGE = 8;
const LOGO_SRC = "https://media.eis-inc.com/m/388d36f30e5ceadc/original/EIS-Standard-SVG.svg";

export interface CartBardcodesStyles {
    wrapper?: InjectableCss;
    barcodeWrapper?: InjectableCss;
    description?: TypographyPresentationProps;
    codeWrapper?: InjectableCss;
    lineInfoWrapper?: InjectableCss;
    lineCardFooter?: InjectableCss;
    printLogo?: IconProps;
    logoWrapper?: InjectableCss;
}

export const cartBardcodesStyles: CartBardcodesStyles = {};

interface Props {
    cartLines?: CartLineModel[] | null;
    msgs: any;
}

const styles = cartBardcodesStyles;

const BarcodeItems: FC<Props> = ({ cartLines, msgs }) => {
    if (!cartLines?.length) {
        return <></>;
    }

    const isFirefoxBrowser = isFirefox();
    const isSafariDesktopBrowser = isSafariDesktop();

    return (
        <StyledWrapper {...styles.wrapper} {...{ isSafariDesktopBrowser, isFirefoxBrowser }}>
            {cartLines.map((line, index) => {
                const positionInPage = index % BOXES_PER_PAGE;
                const isLastInPage = positionInPage === BOXES_PER_PAGE - 1 || positionInPage === BOXES_PER_PAGE - 2;
                const isLeftColumn = index % 2 === 0;
                const qrValue = `${siteMessage("Custom_QR_redirect_link")}?erp=${line.erpNumber}&qty=${line.qtyOrdered}&uom=${line.unitOfMeasure}`;

                return (
                    <StyledWrapper
                        key={line.id}
                        {...styles.barcodeWrapper}
                        {...{ isSafariDesktopBrowser, isFirefoxBrowser, isLastInPage, isLeftColumn }}
                    >
                        <Typography {...styles.description}>{line.shortDescription}</Typography>
                        <StyledWrapper {...styles.codeWrapper}>
                            <StyledWrapper {...styles.logoWrapper}>
                                {isFirefoxBrowser ? (
                                    <Img src={LOGO_SRC} width={100} height={100} />
                                ) : (
                                    <Icon src={LOGO_SRC} {...styles.printLogo} />
                                )}
                            </StyledWrapper>
                            {isFirefoxBrowser ? (
                                <QRCodeCanvas
                                    value={qrValue}
                                    size={100}
                                    style={{ display: "inline-block", float: "right" }}
                                />
                            ) : (
                                <QRCodeSVG
                                    value={qrValue}
                                    height={100}
                                    width={100}
                                    style={{ display: "inline-block", float: "right" }}
                                />
                            )}
                        </StyledWrapper>
                        <StyledWrapper {...styles.lineInfoWrapper}>
                            <StyledWrapper>
                                {line.erpNumber && (
                                    <Typography
                                        css={css`
                                            display: block;
                                        `}
                                    >
                                        {msgs.partNumber} {line.erpNumber}
                                    </Typography>
                                )}
                                {line.properties["specNumber"] && (
                                    <Typography
                                        css={css`
                                            display: block;
                                        `}
                                    >
                                        {msgs.specNumber} {line.properties["specNumber"]}
                                    </Typography>
                                )}
                                {line.manufacturerItem && (
                                    <Typography
                                        css={css`
                                            display: block;
                                        `}
                                    >
                                        {msgs.mfr} {line.manufacturerItem}
                                    </Typography>
                                )}
                                {line.customerName && (
                                    <Typography
                                        css={css`
                                            display: block;
                                        `}
                                    >
                                        {msgs.partNumber} {line.customerName}
                                    </Typography>
                                )}
                            </StyledWrapper>
                            <StyledWrapper>
                                <Typography>
                                    {translate("Qty:")}{" "}
                                    {`${line.qtyOrdered}${line.unitOfMeasure ? `/${line.unitOfMeasure}` : ""}`}
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                );
            })}
        </StyledWrapper>
    );
};

export default BarcodeItems;
